import { SarFormat } from '@today/types'
import { EmptyState, Pagination, SelectField, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { Option, SarRowView, Translate } from './compliance-types'
import { FormAlert } from './form-parts'

/**
 * Props for {@link SubjectAccessListView}.
 *
 * @property rows - The current page, newest first, already formatted.
 * @property message - The last download's refusal, or null.
 * @property onDownload - Present only for a viewer holding `compliance.sar.manage`;
 *   offered on rows whose `downloadable` is true.
 */
export interface SubjectAccessListViewProps {
	t: Translate
	rows: SarRowView[]
	statusFilter: string
	statusOptions: Option[]
	page: number
	pageCount: number
	error: boolean
	message: string | null
	onStatusChange: (status: string) => void
	onPageChange: (page: number) => void
	onRetry: () => void
	onDownload?: (id: string, format: SarFormat) => void
}

const downloadCell = (t: Translate, onDownload: (id: string, format: SarFormat) => void) => (row: SarRowView) =>
	row.downloadable ? (
		<div className="flex gap-2">
			<Button variant="muted" size="sm" onPress={() => onDownload(row.id, SarFormat.JSON)}>
				{t('sars.downloadJson')}
			</Button>
			<Button variant="muted" size="sm" onPress={() => onDownload(row.id, SarFormat.PDF)}>
				{t('sars.downloadPdf')}
			</Button>
		</div>
	) : null

const columns = (t: Translate, onDownload?: (id: string, format: SarFormat) => void): TableColumn<SarRowView>[] => [
	{ key: 'customerId', header: t('sars.columns.customer') },
	{ key: 'identifierTypeLabel', header: t('sars.columns.identifierType') },
	{ key: 'statusLabel', header: t('sars.columns.status') },
	{ key: 'requestedAt', header: t('sars.columns.requested') },
	{ key: 'dueBy', header: t('sars.columns.dueBy') },
	{ key: 'windowEndsAt', header: t('sars.columns.windowEnds') },
	...(onDownload ? [{ key: 'actions', header: '', cell: downloadCell(t, onDownload) }] : []),
]

/** The tenant's subject access requests: status filter, progress, windowed downloads and paging (organism). */
export function SubjectAccessListView(props: SubjectAccessListViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('sars.title')}</h2>
			<SelectField
				label={t('common.status')}
				value={props.statusFilter}
				onValueChange={props.onStatusChange}
				options={props.statusOptions.map((o) => ({ id: o.id, text: o.label }))}
			/>
			<FormAlert message={props.message} />
			{props.error ? (
				<EmptyState title={t('sars.error')} action={{ label: t('common.retry'), onPress: props.onRetry }} />
			) : (
				<Table
					aria-label={t('sars.title')}
					columns={columns(t, props.onDownload)}
					rows={props.rows}
					emptyState={<EmptyState title={t('sars.empty')} />}
				/>
			)}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
		</section>
	)
}
